import { useState, useRef } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Phase 7.5.13: Workflow stages matching backend schema
// Proposal stages are pre-project, project stages are post-conversion
export type WorkflowStage =
  | 'proposal_draft'
  | 'proposal_sent'
  | 'proposal_declined'
  | 'project_setup'
  | 'design_development'
  | 'permit_preparation'
  | 'in_permit_review'
  | 'construction_administration'
  | 'on_hold'
  | 'complete';

interface WorkflowStageOption {
  value: WorkflowStage;
  label: string;
}

// Phase 7.5.13: Updated workflow stages with proposal phases
const workflowStages: WorkflowStageOption[] = [
  { value: 'proposal_draft', label: 'Proposal: Draft' },
  { value: 'proposal_sent', label: 'Proposal: Sent' },
  { value: 'proposal_declined', label: 'Proposal: Declined' },
  { value: 'project_setup', label: 'Project Setup' },
  { value: 'design_development', label: 'Design Development' },
  { value: 'permit_preparation', label: 'Permit Preparation' },
  { value: 'in_permit_review', label: 'In Permit Review' },
  { value: 'construction_administration', label: 'Construction Administration' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'complete', label: 'Complete' },
];

interface WorkflowStageSelectProps {
  value: WorkflowStage | undefined;
  onChange: (value: WorkflowStage) => void;
  onCancel?: () => void;
  disabled?: boolean;
  className?: string;
}

export function WorkflowStageSelect({
  value,
  onChange,
  onCancel,
  disabled = false,
  className,
}: WorkflowStageSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const valueChangedRef = useRef(false);

  const handleValueChange = (newValue: WorkflowStage) => {
    valueChangedRef.current = true;
    onChange(newValue);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);

    // If closing and no value was changed, call onCancel
    if (!open && !valueChangedRef.current && onCancel) {
      onCancel();
    }

    // Reset flag when opening
    if (open) {
      valueChangedRef.current = false;
    }
  };

  return (
    <Select
      value={value}
      onValueChange={handleValueChange}
      onOpenChange={handleOpenChange}
      open={isOpen}
      disabled={disabled}
    >
      <SelectTrigger className={className}>
        <SelectValue placeholder="Select workflow stage" />
      </SelectTrigger>
      <SelectContent>
        {workflowStages.map((stage) => (
          <SelectItem key={stage.value} value={stage.value}>
            {stage.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
